import { ChangeDetectionStrategy, Component, computed, DestroyRef, Inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import {
  ContainerComponent,
  ShadowOnScrollDirective,
  SidebarBrandComponent,
  SidebarComponent,
  SidebarFooterComponent,
  SidebarHeaderComponent,
  SidebarNavComponent,
} from '@coreui/angular-pro';
import { IconDirective } from '@coreui/icons-angular';
import { AuthService } from 'auth';
import { I18nService } from 'i18n';
import { NgScrollbar } from 'ngx-scrollbar';
import { ENVIRONMENT, isAdmin as isAdminUser, isSuperAdmin as isSuperAdminUser, Logger, LogoComponent } from 'shared';
import { DefaultHeaderComponent } from '.';
import { ImpersonationBannerComponent } from '../../../components/impersonation-banner';
import { CookieSettingsLinkComponent } from '../../../components/cookie-consent/cookie-settings-link.component';
import { contentTransition } from '../../../animations/content-transition';
import { MenuService } from '../../../services/menu.service';

function isOverflown(element: HTMLElement) {
  return (
    element.scrollHeight > element.clientHeight ||
    element.scrollWidth > element.clientWidth
  );
}

@Component({
  selector: 'app-default-layout',
  templateUrl: './default-layout.component.html',
  styleUrls: ['./default-layout.component.scss'],
  imports: [
    SidebarComponent,
    SidebarHeaderComponent,
    SidebarBrandComponent,
    SidebarNavComponent,
    SidebarFooterComponent,
    ContainerComponent,
    DefaultHeaderComponent,
    ImpersonationBannerComponent,
    CookieSettingsLinkComponent,
    IconDirective,
    NgScrollbar,
    RouterOutlet,
    RouterLink,
    ShadowOnScrollDirective,
    LogoComponent
  ],
  animations: [contentTransition],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DefaultLayoutComponent {
  protected logger = new Logger('DefaultLayoutComponent');
  mainMenuSidebar = viewChild<SidebarComponent>('mainMenuSidebar');
  public navItems$ = toSignal(this.menuService.navItems$);
  public version = this.environment.version;
  showUserEditModal = false;
  badge = signal<string | null>(null);
  showBadge = computed(() => {
    const badge = this.badge();
    if (badge) {
      const sidebarClasses = this.mainMenuSidebar()?.hostClasses()!;
      if (sidebarClasses.hide) {
        return true;
      }
    }
    return false;
  });

  constructor(
    private i18nService: I18nService,
    private router: Router,
    private menuService: MenuService<any>,
    private destroyRef: DestroyRef,
    private authService: AuthService,
    @Inject(ENVIRONMENT) private environment: any
  ) {
    this.logger.debug('DefaultLayoutComponent.ctor');
    this.menuService.badge$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(badge => this.badge.set(badge))

    // Subscribe to user changes and filter menu items based on admin role and user ID
    this.authService.getUser$().subscribe(user => {
      this.logger.debug('DefaultLayoutComponent: User changed', user);
      const isAdmin = isAdminUser(user);
      const isSuperAdmin = isSuperAdminUser(user);
      const userId = user?.id;
      const isXshopAdmin = user?.xshop_admin ?? false;
      this.logger.debug('DefaultLayoutComponent: isAdmin =', isAdmin, 'isSuperAdmin =', isSuperAdmin, 'userId =', userId, 'user_type =', user?.user_type, 'isXshopAdmin =', isXshopAdmin);
      this.menuService.filterNavItemsByRole(isAdmin, userId, isXshopAdmin, isSuperAdmin, {
        community_enabled: user?.community_enabled,
        // Opt-in flag (Global Health "My Family"): only an explicit true shows the
        // entry, so a backend or brand that does not send it never shows it.
        family_enabled: user?.family_enabled === true
      }, user?.community_roles ?? []);
    });
  }

  policy(event: Event) {
    event.stopPropagation();
    this.router
      .navigateByUrl(`${this.i18nService.getLanguage()}/media/policy`)
      .then(() => {
        const sidebar = this.mainMenuSidebar();
        if (sidebar) {
          const state = sidebar.sidebarState;
          state.visible = true;
          sidebar.sidebarState = state;
        }
      });
  }

  getAnimationState(outlet: any) {
    return outlet?.activatedRouteData?.['animation'] || 'default';
  }
}